import React, {
  type ComponentType,
  type CSSProperties,
  type PropsWithChildren,
  type ReactNode,
  type RefObject,
} from 'react';
import ReactDOM from 'react-dom';

import classNames from 'classnames';

import type { GenericHtmlProps } from '../../types/common';
import Tooltip from '../../ui/tooltip/tooltip';
import { fullscreenElementOrBody } from '../../utils/fullscreen_element_or_body';
import isTouchDevice from '../../utils/is_touch_device';
import {
  findAncestor,
  setTooltipPosition,
  type TooltipPlacement,
} from './utils';

import styles from './tooltip_trigger.module.css';

// Add this type and use these props in any custom component to add
// tooltips support for it
export type TooltipWrappedProps = {
  onMouseOver?: (event: React.MouseEvent<HTMLElement>) => void;
  onMouseLeave?: () => void;
  'data-tooltip'?: string;
};

export type Props<P> = PropsWithChildren<{
  show?: boolean;
  disabled?: boolean;
  component: ComponentType<P> | string;
  componentProps: P;
  placement: TooltipPlacement;
  shouldShowTooltip: (element: HTMLElement) => boolean;
  tooltipText: string | ReactNode;
  tooltipClass?: string;
  // If true the tooltip trigger will make its own component wrapper
  // and will handle mouse events itself.
  ownWrapper: boolean;
  // force preferredPlacement regardless tooltip's position.
  // useful when using custom css classes
  forcePlacement?: boolean;
  className?: string;
  showTooltipOnTouchDevice?: boolean;
}>;

type State = {
  style: CSSProperties;
  tooltip?: boolean;
  placement?: TooltipPlacement;
};

class TooltipTrigger<P = GenericHtmlProps> extends React.Component<
  Props<P>,
  State
> {
  static defaultProps = {
    placement: 'top',
    component: 'div',
    componentProps: {},
    shouldShowTooltip: (): boolean => true,
    ownWrapper: false,
  };

  ref?: RefObject<HTMLDivElement | null>;

  state: State = {
    style: { position: 'fixed' },
  };

  componentWillUnmount(): void {
    window.removeEventListener('scroll', this.onScroll, true);
  }

  componentDidUpdate(prevProps: Props<P>): void {
    if (this.props.show !== prevProps.show) {
      this.setState({ tooltip: this.props.show });
    }
  }

  clearTooltip = (): void => {
    if (this.props.disabled || this.props.show) {
      return;
    }
    window.removeEventListener('scroll', this.onScroll, true);
    this.setState({ tooltip: false });
  };

  onScroll = (): void => {
    if (this.state.tooltip) {
      this.setState({ tooltip: false });
    }
  };

  setTooltip = (e: React.MouseEvent<HTMLElement>): void => {
    if (this.props.disabled) {
      return;
    }
    const target = e.currentTarget as HTMLElement;
    const trigger = findAncestor(target);
    if (trigger) {
      const showTooltip = this.props.shouldShowTooltip(trigger);
      if (showTooltip) {
        if (!this.ref) {
          this.ref = React.createRef<HTMLDivElement>();
        }

        window.addEventListener('scroll', this.onScroll, true);
        this.setState({ tooltip: true }, () => {
          const tooltipPosition = setTooltipPosition(
            trigger,
            this.ref ? this.ref.current : null,
            this.props.placement,
            this.props.forcePlacement,
          );
          const style: CSSProperties = {
            top: tooltipPosition.top,
            left: tooltipPosition.left,
            position: 'fixed',
          };
          const placement = tooltipPosition.placement;
          this.setState({ style, placement });
        });
      }
    }
  };

  render(): ReactNode {
    const {
      children,
      component: Component,
      tooltipClass,
      componentProps,
      ownWrapper,
      className,
      showTooltipOnTouchDevice,
    } = this.props;

    // Touch devices doesn't have tooltips, this protects
    // from clicking tooltip wrapped component twice on touch devices
    if (isTouchDevice() && !showTooltipOnTouchDevice) {
      return (
        <Component {...componentProps} onMousePress={this.setTooltip}>
          {children}
        </Component>
      );
    }

    const triggerProps =
      isTouchDevice() && showTooltipOnTouchDevice
        ? {
            onClick: (event: any) => {
              this.setTooltip(event);
              window.setTimeout(() => {
                this.clearTooltip();
              }, 4000);
            },
            'data-tooltip': 'true',
          }
        : {
            onMouseOver: this.setTooltip,
            onMouseLeave: this.clearTooltip,
            'data-tooltip': 'true',
          };
    const componentContent = !ownWrapper ? (
      <Component {...componentProps} {...triggerProps}>
        {children}
      </Component>
    ) : (
      <div className={classNames(className, styles.wrapper)} {...triggerProps}>
        <Component {...componentProps}>{children}</Component>
      </div>
    );

    return (
      <>
        {this.state.tooltip &&
          this.props.tooltipText &&
          ReactDOM.createPortal(
            <Tooltip
              fixed
              placement={this.state.placement}
              style={this.state.style}
              className={tooltipClass}
              elRef={this.ref}
            >
              {this.props.tooltipText}
            </Tooltip>,
            fullscreenElementOrBody(),
          )}
        {componentContent}
      </>
    );
  }
}

export default TooltipTrigger;
